import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { Drawer, Tabs, Tab, Typography, IconButton, Chip } from '@material-ui/core'
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons'
import ContextCreateModal from './ContextCreateModal'
import AttentionTab from './AttentionTab'
import NotesTab from './NotesTab'
import TasksTab from './TasksTab'
import BlockersTab from './BlockersTab'
import DecisionsTab from './DecisionsTab'
import ReviewsTab from './ReviewsTab'
import ConversationsTab from './ConversationsTab'

const TAB_KEYS = ['attention', 'notes', 'tasks', 'blockers', 'decisions', 'reviews', 'conversations']

const ContextDrawer = ({ open, onClose, projectId, initialTab, parentFilter, url }) => {
  const [activeTab, setActiveTab] = React.useState(initialTab || 'attention')
  const [summary, setSummary] = React.useState(null)
  const [data, setData] = React.useState({ notes: [], tasks: [], blockers: [], decisions: [], reviewEvents: [], conversations: [] })
  const [loading, setLoading] = React.useState({})
  const [fetched, setFetched] = React.useState({})
  const [createOpen, setCreateOpen] = React.useState(false)
  const [createMode, setCreateMode] = React.useState('note')
  const [editRecord, setEditRecord] = React.useState(null)

  // Reset activeTab when initialTab changes
  React.useEffect(() => {
    setActiveTab(initialTab || 'attention')
  }, [initialTab])

  // Build query string for parent filter
  const parentQS = parentFilter
    ? `?parent_type=${encodeURIComponent(parentFilter.parent_type)}&parent_id=${encodeURIComponent(parentFilter.parent_id)}`
    : ''

  // Fetch summary on open
  React.useEffect(() => {
    if (open) {
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/context/summary${parentQS}`).then(res => {
        setSummary(res.data)
      }).catch(err => {
        console.error('Failed to fetch context summary:', err)
      })
      // Reset fetched state so tabs re-fetch on re-open
      setFetched({})
    }
  }, [open, projectId])

  // Lazy-load tab data on first selection
  React.useEffect(() => {
    if (!open || activeTab === 'attention') return
    if (fetched[activeTab]) return

    const endpointMap = {
      notes: 'notes',
      tasks: 'tasks',
      blockers: 'blockers',
      decisions: 'decisions',
      reviews: 'review-events',
      conversations: 'conversations'
    }
    const dataKey = {
      notes: 'notes',
      tasks: 'tasks',
      blockers: 'blockers',
      decisions: 'decisions',
      reviews: 'reviewEvents',
      conversations: 'conversations'
    }
    const endpoint = endpointMap[activeTab]
    if (!endpoint) return

    setLoading(prev => ({ ...prev, [activeTab]: true }))
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/${endpoint}${parentQS}`).then(res => {
      const items = Array.isArray(res.data) ? res.data : (res.data.data || res.data[dataKey[activeTab]] || [])
      setData(prev => ({ ...prev, [dataKey[activeTab]]: items }))
      setFetched(prev => ({ ...prev, [activeTab]: true }))
      setLoading(prev => ({ ...prev, [activeTab]: false }))
    }).catch(err => {
      console.error(`Failed to fetch ${activeTab}:`, err)
      setLoading(prev => ({ ...prev, [activeTab]: false }))
    })
  }, [activeTab, open])

  const refreshTab = () => {
    setFetched(prev => ({ ...prev, [activeTab]: false }))
    // Also refresh summary
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/context/summary${parentQS}`).then(res => {
      setSummary(res.data)
    }).catch(() => {})
  }

  const handleCreateSaved = () => {
    setCreateOpen(false)
    setEditRecord(null)
    refreshTab()
  }

  const openCreate = (mode) => {
    setCreateMode(mode)
    setEditRecord(null)
    setCreateOpen(true)
  }

  const openEdit = (mode, record) => {
    setCreateMode(mode)
    setEditRecord(record)
    setCreateOpen(true)
  }

  const handleResolveBlocker = (blocker) => {
    const resolutionSummary = window.prompt('Resolution summary:')
    if (!resolutionSummary) return
    axios.put(`${url}/api/bid-estimation/blockers/${blocker._id}/resolve`, { resolution_summary: resolutionSummary }).then(() => {
      refreshTab()
    }).catch(err => console.error('Failed to resolve blocker:', err))
  }

  const handleSupersedeDecision = (decision) => {
    if (!window.confirm('Supersede this decision?')) return
    axios.put(`${url}/api/bid-estimation/decisions/${decision._id}/supersede`, {}).then(() => {
      refreshTab()
    }).catch(err => console.error('Failed to supersede decision:', err))
  }

  const handleCompleteReview = (review) => {
    const outcome = window.prompt('Outcome summary (optional):')
    if (outcome === null) return
    axios.put(`${url}/api/bid-estimation/review-events/${review._id}/complete`, { outcome_summary: outcome }).then(() => {
      refreshTab()
    }).catch(err => console.error('Failed to complete review:', err))
  }

  const counts = summary?.counts || {}

  const tabLabel = (label, count) => {
    if (count > 0) return `${label} (${count})`
    return label
  }

  const tabModeMap = {
    notes: 'note',
    tasks: 'task',
    blockers: 'blocker',
    decisions: 'decision',
    reviews: 'review_event',
    conversations: 'conversation'
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'attention':
        return <AttentionTab items={summary?.needsAttention || []} onSwitchTab={setActiveTab} />
      case 'notes':
        return <NotesTab items={data.notes || []} loading={loading.notes} onEdit={openEdit} />
      case 'tasks':
        return <TasksTab items={data.tasks || []} loading={loading.tasks} onEdit={openEdit} />
      case 'blockers':
        return <BlockersTab items={data.blockers || []} loading={loading.blockers} onEdit={openEdit} onResolve={handleResolveBlocker} />
      case 'decisions':
        return <DecisionsTab items={data.decisions || []} loading={loading.decisions} onEdit={openEdit} onSupersede={handleSupersedeDecision} />
      case 'reviews':
        return <ReviewsTab items={data.reviewEvents || []} loading={loading.reviews} onEdit={openEdit} onComplete={handleCompleteReview} />
      case 'conversations':
        return <ConversationsTab items={data.conversations || []} loading={loading.conversations} onEdit={openEdit} />
      default: return null
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div style={{ width: 480, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--line, #e0e0e0)' }}>
          <Typography variant="h6" style={{ fontWeight: 600, fontSize: 16 }}>
            Estimate Context
            {parentFilter?.parent_label && (
              <Chip label={parentFilter.parent_label} size="small" variant="outlined" style={{ marginLeft: 8, height: 20, fontSize: 11 }} />
            )}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {activeTab !== 'attention' && tabModeMap[activeTab] && (
              <IconButton size="small" onClick={() => openCreate(tabModeMap[activeTab])} title={`Add ${activeTab}`}>
                <AddIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={TAB_KEYS.indexOf(activeTab) >= 0 ? TAB_KEYS.indexOf(activeTab) : 0}
          onChange={(_e, v) => setActiveTab(TAB_KEYS[v])}
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          style={{ borderBottom: '1px solid var(--line, #e0e0e0)', minHeight: 36 }}
        >
          <Tab label={tabLabel('Attention', (summary?.needsAttention || []).length)} style={{ minHeight: 36, minWidth: 0, padding: '4px 10px', fontSize: 11, textTransform: 'none' }} />
          <Tab label={tabLabel('Notes', counts.notes || 0)} style={{ minHeight: 36, minWidth: 0, padding: '4px 10px', fontSize: 11, textTransform: 'none' }} />
          <Tab label={tabLabel('Tasks', counts.tasksOpen || 0)} style={{ minHeight: 36, minWidth: 0, padding: '4px 10px', fontSize: 11, textTransform: 'none' }} />
          <Tab label={tabLabel('Blockers', counts.blockersOpen || 0)} style={{ minHeight: 36, minWidth: 0, padding: '4px 10px', fontSize: 11, textTransform: 'none' }} />
          <Tab label={tabLabel('Decisions', counts.decisions || 0)} style={{ minHeight: 36, minWidth: 0, padding: '4px 10px', fontSize: 11, textTransform: 'none' }} />
          <Tab label={tabLabel('Reviews', counts.reviewsUpcoming || 0)} style={{ minHeight: 36, minWidth: 0, padding: '4px 10px', fontSize: 11, textTransform: 'none' }} />
          <Tab label={tabLabel('Conversations', counts.conversations || 0)} style={{ minHeight: 36, minWidth: 0, padding: '4px 10px', fontSize: 11, textTransform: 'none' }} />
        </Tabs>

        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {renderTabContent()}
        </div>
      </div>

      {/* Create / Edit Modal */}
      <ContextCreateModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setEditRecord(null) }}
        onSaved={handleCreateSaved}
        projectId={projectId}
        mode={createMode}
        parentContext={parentFilter}
        editRecord={editRecord}
      />
    </Drawer>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(ContextDrawer)
